import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import HighlightedText from "components/Shared/HighlightedText";

import { useLazyListAccountQuery } from "store/services/accountApi";

import { useAppMeta, useTablePagination } from "hooks";
import TableFilter from "components/Shared/AppTable/TableFilter";

const AccountListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Accounts" }];

  const accountListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Name",
      selector: (row) => row.name,
      cell: (row) => (
        <Link
          title={row?.name}
          to={`/app/crm/account/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.name ? (
            <HighlightedText text={row?.name} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Display Name",
      selector: (row) => row?.displayName,
      cell: (row) =>
        row?.displayName ? (
          <HighlightedText text={row?.displayName} highlight={keyword} />
        ) : (
          "N/A"
        ),
    },
    {
      name: "Email",
      selector: (row) => row?.email,
      cell: (row) => (
        <a
          href={`mailto:${row?.email}`}
          className="hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.email ? (
            <HighlightedText text={row?.email} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </a>
      ),
    },
    { name: "Type", selector: (row) => row?.type },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/account/${row._id}/detail`}>
            <IconButton size="small" aria-label="View User" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>

          <Link to={`/app/crm/account/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit User">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [fetchAccounts, { data: accountRes, isLoading, isFetching, error }] =
    useLazyListAccountQuery();

  React.useEffect(() => {
    fetchAccounts({
      active: activeTab !== "Inactive",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchAccounts, keyword, perPage]);

  useAppMeta(`${activeTab} Accounts`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={accountListTabs} activeTab={activeTab} />

        <TableFilter
          searchLabel="Name/Display Name/Email"
          disabled={isLoading || isFetching}
        >
          <div className="font-medium"> {activeTab} Accounts </div>
        </TableFilter>

        <AppTable
          columns={columns}
          data={accountRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={accountRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default AccountListScreen;

const PageActions = () => {
  return (
    <>
      <Link to="/app/crm/account/create">
        <Button startIcon={<Add />} variant="contained">
          New Account
        </Button>
      </Link>
    </>
  );
};
